/* eslint-disable react/prop-types */
import React from 'react';
import { bindActionCreators } from 'redux';
import * as actionCreators from '@src/store/actions';
import { withRouter } from 'next/router';
import { connect } from 'react-redux';
import last from 'lodash/last';
import split from 'lodash/split';

import { checkIsServer } from '@src/utils/deprecatedCommon';
import Header from '@src/components/home/header';
import Footer from '@src/components/home/Footer';
import BusinessFooter from '@src/components/business/footer';
import BottomKeepSpace from '@src/components/bottomKeepSpace';
import ErrorPage from '@src/components/ErrorPage';
import NotFoundIcon from '@src/components/common/icons/NotFoundIcon';
import config from '@src/config';
import { resolveRedirectTarget } from '@src/const/redirectConfigs';
import { getThemeSelector } from '@src/store/selectors';
import { PUNCH_MACHINE_DEMO_PATH, isPunchMachinePath } from '@src/components/hr/punchInMachine/constants';

import classStyle from './AppBackground.module.less';

import SideMenuWrapper from './SideMenuWrapper';
import BusinessWrapper from './BusinessWrapper';
import HrBusinessWrapper from './HrBusinessWrapper';

const { isTest, type } = config;

class AppBackground extends React.PureComponent {
	constructor(props) {
		super(props);

		const {
			router: { pathname },
		} = props;

		if (!checkIsServer() && !isTest) {
			window.onerror = this.handleError;
		}

		// 取得參數判斷
		if (!checkIsServer()) {
			this.handleHistoryParams();
		}

		this.state = {
			pathname,
		};
	}

	// 傳送錯誤
	handleError = (msg, url, lineNo, columnNo, error) => {
		const { actions } = this.props;
		const string = msg.toLowerCase();
		const substring = 'script error';
		let errorMessage = '';

		if (typeof msg === 'string' && msg.startsWith('ResizeObserver loop')) {
			return;
		}

		if (string.indexOf(substring) > -1) {
			errorMessage = {
				type,
				msg: 'Script Error: See Browser Console for Detail',
			};
		} else {
			errorMessage = {
				type,
				msg,
				url,
				lineNo,
				columnNo,
				errorObject: JSON.stringify(error),
			};

			if (lineNo !== 1 && columnNo !== 1) {
				actions.sendBugMsgCall({ msg: JSON.stringify(errorMessage) });
			}
		}

		return false;
	};

	handleHistoryParams = () => {
		const { actions, router } = this.props;

		const { pathname, query } = router;

		// 處理oauth登入
		if (pathname.indexOf('/ext/oauth/') !== -1) {
			const name = last(split(pathname, '/'));

			actions.socialLoginCall({
				params: query,
				name,
			});

			return;
		}

		if (pathname === '/action' || pathname === '/admin/action') {
			// http://localhost:3000/action?action=check_login_session&code=<exchange_code>

			const { action, verification_code, code, cb, line_login_state: lineLoginState } = query;

			switch (action) {
			case 'check_email':
				actions.emailConfirmCall({
					verification_code,
				});
				break;
			case 'check_forget_password':
				actions.getForgetPasswordEmailCall({
					verification_code,
				});
				break;
			case 'check_business_forget_password':
				// 舊版重設密碼流程已移除，導回登入頁並提示連結已過期
				actions.historyPushCall({ pathname: '/business-login', search: '?toast=reset_password_link_expired' });
				break;
			case 'google_domain_not_allow':
				actions.googleDomainNotAllow();
				break;
			case 'line_hr_binding_not_found':
				actions.lineHrBindingNotFound({ lineLoginState });
				break;
			case 'check_invitation':
				// staff_invitation
				break;
			case 'check_login_session':
				// code 換 JWT 後才會接續 checkLoginSessionCall（見 oauthExchangeCall epic）
				actions.oauthExchangeCall({ code, cb, lineLoginState });
				break;
			default:
				break;
			}
		}
	};

	render() {
		const {
			children,
			showPageLoading,
			homePage,
			headerContent,
			footerContent,
			router,
			themeState,
		} = this.props;
		const { closedWeb } = this.props; // 當 closedWeb = true，該學校為封閉學校
		const { pathname } = router;

		const isActionPage = pathname === '/action';
		const isBusinessAuthPage = pathname.indexOf('/business') > -1;
		const isHrBusinessAuthPage = pathname === '/business-login' || pathname === '/business-forget-password';
		const isHrRegistrationLinkPage = pathname.indexOf('/hr/registration-link') > -1;
		// 卡機打卡與其 demo 頁都走無版面（不套 header / footer / side menu）
		const isPunchMachinePage = isPunchMachinePath(pathname);
		const isHrPage = pathname.indexOf('/hr') > -1;
		const isShortLinkPage = pathname.startsWith('/r/');
		const isHrRelatedPage = isHrPage || isHrBusinessAuthPage || isShortLinkPage;
		const isQRcodePage = pathname.indexOf('/qrcode') > -1;

		// 公開 demo 不依賴租戶 theme，避免前一頁的錯誤狀態擋住展示。
		if (pathname === PUNCH_MACHINE_DEMO_PATH) return children;

		if (themeState === 'NOT_FOUND') {
			if (!checkIsServer()) {
				// SSG 頁面（如 /hr）沒走 SSR 的 server 301，這裡在 client 端補做：
				// 對到 redirectConfigs 的子網域（org 搬家）導去新位置，其餘 fallback 平台首頁。
				window.location.replace(resolveRedirectTarget(window.location.origin));
			}
		} else if (themeState === 'PAYMENT_REQUIRED' || (themeState === 'FAIL' && !isHrPage)) {
			// /hr*：theme API 掛掉（5xx / timeout / 無快取）時不要擋成 404，改用預設 theme 繼續 render，讓使用者還能離線打卡。
			// apiResponseCache 命中時 themeState 會是 SUCCESS，不會走到這。
			return (
				<ErrorPage title="404" messages={['我們暫時找不到這個頁面，', '請重整頁面或稍後再試。']}>
					<NotFoundIcon />
				</ErrorPage>
			);
		}

		if (isPunchMachinePage) {
			return children;
		}

		if (showPageLoading || (!homePage && !isHrRelatedPage)) {
			return null;
		}
		const { hide: hideHeader } = headerContent;
		const { hide: hideFooter } = footerContent;
		const isBusinessPageHideHeader = isBusinessAuthPage && closedWeb;
		const showHeader = !hideHeader && !isActionPage && !isBusinessPageHideHeader;
		const showFooter = !hideFooter && !isActionPage;
		const showBusinessFooter = closedWeb && !isActionPage && !isBusinessAuthPage;

		if (isShortLinkPage) {
			return children;
		}
		if (isHrRegistrationLinkPage || isHrBusinessAuthPage) {
			return (
				<HrBusinessWrapper>
					{children}
				</HrBusinessWrapper>
			);
		}
		if (isHrPage) {
			return (
				<SideMenuWrapper>
					{children}
				</SideMenuWrapper>
			);
		}
		if (isBusinessAuthPage || isQRcodePage) {
			return (
				<BusinessWrapper>
					{children}
				</BusinessWrapper>
			);
		}
		return (
			<div id="container" className={classStyle.container}>
				<BottomKeepSpace>
					{showHeader && <Header />}
					{children}
					{showFooter && <Footer />}
					{showBusinessFooter && <BusinessFooter />}
				</BottomKeepSpace>
			</div>
		);
	}
}

function mapStateToProps(state) {
	const { session } = state;
	const {
		showPageLoading,
		homePage,
		headerContent,
		footerContent,
		themeState,
		themes: {
			closed_web,
		},
	} = getThemeSelector(state);

	return {
		session,
		showPageLoading,
		homePage,
		headerContent,
		footerContent,
		themeState,
		closedWeb: closed_web,
	};
}

function mapDispatchToProps(dispatch) {
	return {
		actions: bindActionCreators({ ...actionCreators }, dispatch),
	};
}

export default withRouter(
	connect(mapStateToProps, mapDispatchToProps)(AppBackground),
);
